import { useEffect, useRef, useState } from "react";
import { Stepper } from "@JohnLewisPartnership/jl-design-system/dist/components/stepper";
import styles from "./LengthSelector.module.scss";
import {
  ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
  ADD_TO_BASKET_ESI_LENGTH_UPDATED,
} from "../../constants/constants";

const inputId = "length";

type Props = {
  /**
   * A boolean indicating whether the length selector is disabled.
   */
  disabled?: boolean;
  /**
   * A number representing the minimum length that can be selected.
   */
  minLength?: number;
  /**
   * A number representing the maximum roll length.
   */
  fabricRollLength?: number;
};

/**
 * Displays a length selector for the product.
 */
export const LengthSelectorUI = ({
  disabled = false,
  minLength = 1,
  fabricRollLength = undefined,
}: Props) => {
  const [length, setLength] = useState<number>(minLength);
  const [isMaxLengthMessage, setIsMaxLengthMessage] = useState<boolean>(false);
  const hasMountedRef = useRef<boolean>(false);

  useEffect(() => {
    const handleResetLengthSelectorState = () => {
      setLength(minLength);
    };

    window.addEventListener(
      ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
      handleResetLengthSelectorState,
    );

    return () => {
      window.removeEventListener(
        ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
        handleResetLengthSelectorState,
      );
    };
  }, []);

  useEffect(() => {
    if (hasMountedRef.current) {
      const e = new CustomEvent(ADD_TO_BASKET_ESI_LENGTH_UPDATED, {
        detail: {
          quantity: length,
        },
      });
      window.dispatchEvent(e);
    } else {
      hasMountedRef.current = true;
    }
  }, [length]);

  useEffect(() => {
    if (!!fabricRollLength && length > fabricRollLength) {
      setIsMaxLengthMessage(true);
    } else {
      setIsMaxLengthMessage(false);
    }
  }, [length, fabricRollLength]);

  return (
    <div data-testid="product:length-selector" data-component="LengthSelector">
      <p>Fabric Length. Minimum order length {minLength}m</p>
      <div className={styles.lengthSelector}>
        <label htmlFor={inputId}>Metre(s)</label>
        <Stepper
          data-testid="stepper"
          value={length}
          disabled={disabled}
          inputId={inputId}
          handleChange={(value) => setLength(value)}
          inputName={"length-selector"}
          min={minLength}
          max={999}
        />
      </div>
      {isMaxLengthMessage ? (
        <p className={styles.lengthSelector__maxLengthMessage}>
          Maximum roll length is {fabricRollLength} metres. Fabric will come in
          1 or more pieces.
        </p>
      ) : null}
    </div>
  );
};
